import { Dayjs } from "dayjs";
import {
  createContext,
  ReactNode,
  use,
  useCallback,
  useMemo,
  useState,
} from "react";
import { isFetchBaseQueryError } from "helpers";
import { useTenant } from "hooks/settings";
import { useDispatch } from "react-redux";
import {
  generatedApi,
  LocationExceptionalStatusRead,
  LocationExceptionalStatusType,
  useCreateLocationExceptionalStatusMutation,
  useDeleteLocationExceptionalStatusMutation,
  useListLocationExceptionalStatusesQuery,
  useUpdateLocationExceptionalStatusMutation,
} from "src/store/api/generatedApi";

export type SubmitError = "overlap" | "unknown" | null;

export interface ExceptionDraft {
  status: LocationExceptionalStatusType;
  startedAt: Dayjs | null;
  endedAt: Dayjs | null;
  note: string;
}

export const EMPTY_DRAFT: ExceptionDraft = {
  status: "ignorable",
  startedAt: null,
  endedAt: null,
  note: "",
};

type DialogMode = "create" | "edit";

interface ExceptionDraftContextValue {
  location: string;
  exceptionalStatuses: LocationExceptionalStatusRead[];
  hasUnsavedDraft: boolean;
  mode: DialogMode | null;
  editingException: LocationExceptionalStatusRead | null;
  openCreate: () => void;
  openEdit: (exception: LocationExceptionalStatusRead) => void;
  closeDialog: () => void;
  draft: ExceptionDraft;
  patchDraft: (patch: Partial<ExceptionDraft>) => void;
  pendingDeleteId: number | null;
  requestDelete: (id: number) => void;
  cancelDelete: () => void;
  confirmDelete: () => Promise<void>;
  submit: (values: ExceptionDraft) => Promise<void>;
  isSubmitting: boolean;
  submitError: SubmitError;
}

const ExceptionDraftContext = createContext<ExceptionDraftContextValue | null>(
  null
);

export const useExceptionDraft = (): ExceptionDraftContextValue => {
  const context = use(ExceptionDraftContext);
  if (context === null) {
    throw new Error(
      "useExceptionDraft must be used within an ExceptionDraftProvider"
    );
  }
  return context;
};

export const useExceptionDraftOptional =
  (): ExceptionDraftContextValue | null => use(ExceptionDraftContext);

export const ExceptionDraftProvider = ({
  location,
  children,
}: {
  location: string;
  children: ReactNode;
}) => {
  const tenantName = useTenant();
  const dispatch = useDispatch();
  const [mode, setMode] = useState<DialogMode | null>(null);
  const [editingException, setEditingException] =
    useState<LocationExceptionalStatusRead | null>(null);
  const [draft, setDraft] = useState<ExceptionDraft>(EMPTY_DRAFT);
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
  const [submitError, setSubmitError] = useState<SubmitError>(null);

  const { data: statusesData } = useListLocationExceptionalStatusesQuery({
    tenantName,
  });
  const exceptionalStatuses = useMemo(
    () =>
      (statusesData?.items ?? []).filter(
        (item) => item.location_name === location
      ),
    [statusesData, location]
  );

  const hasUnsavedDraft =
    mode !== null ||
    draft.startedAt !== null ||
    draft.endedAt !== null ||
    draft.note !== "";

  const [createStatus, { isLoading: isCreating }] =
    useCreateLocationExceptionalStatusMutation();
  const [updateStatus, { isLoading: isUpdating }] =
    useUpdateLocationExceptionalStatusMutation();
  const [deleteStatus] = useDeleteLocationExceptionalStatusMutation();

  const patchDraft = useCallback((patch: Partial<ExceptionDraft>) => {
    setDraft((previous) => ({ ...previous, ...patch }));
  }, []);

  const discardDraft = useCallback(() => {
    setDraft(EMPTY_DRAFT);
  }, []);

  const openCreate = useCallback(() => {
    setSubmitError(null);
    setEditingException(null);
    setMode("create");
  }, []);

  const openEdit = useCallback((exception: LocationExceptionalStatusRead) => {
    setSubmitError(null);
    setEditingException(exception);
    setMode("edit");
  }, []);

  const closeDialog = useCallback(() => {
    setMode(null);
    setEditingException(null);
  }, []);

  const requestDelete = useCallback((id: number) => {
    setPendingDeleteId(id);
  }, []);

  const cancelDelete = useCallback(() => {
    setPendingDeleteId(null);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (pendingDeleteId === null) {
      return;
    }
    try {
      await deleteStatus({ tenantName, statusId: pendingDeleteId }).unwrap();
      dispatch(
        generatedApi.util.invalidateTags(["location_exceptional_status"])
      );
    } finally {
      setPendingDeleteId(null);
    }
  }, [deleteStatus, dispatch, tenantName, pendingDeleteId]);

  const submit = useCallback(
    async (values: ExceptionDraft) => {
      if (values.startedAt === null || values.endedAt === null) {
        return;
      }
      const body = {
        started_at: values.startedAt.utc().toISOString(),
        ended_at: values.endedAt.utc().toISOString(),
        status: values.status,
        note: values.note || null,
      };
      setSubmitError(null);
      try {
        if (mode === "edit" && editingException !== null) {
          await updateStatus({
            tenantName,
            statusId: editingException.id,
            locationExceptionalStatusUpdate: body,
          }).unwrap();
        } else {
          await createStatus({
            tenantName,
            locationExceptionalStatusCreate: {
              ...body,
              location_name: location,
            },
          }).unwrap();
          discardDraft();
        }
        dispatch(
          generatedApi.util.invalidateTags(["location_exceptional_status"])
        );
        closeDialog();
      } catch (error) {
        setSubmitError(
          isFetchBaseQueryError(error) && error.status === 409
            ? "overlap"
            : "unknown"
        );
      }
    },
    [
      mode,
      editingException,
      tenantName,
      location,
      createStatus,
      updateStatus,
      discardDraft,
      closeDialog,
      dispatch,
    ]
  );

  const value = useMemo<ExceptionDraftContextValue>(
    () => ({
      location,
      exceptionalStatuses,
      hasUnsavedDraft,
      mode,
      editingException,
      openCreate,
      openEdit,
      closeDialog,
      draft,
      patchDraft,
      pendingDeleteId,
      requestDelete,
      cancelDelete,
      confirmDelete,
      submit,
      isSubmitting: isCreating || isUpdating,
      submitError,
    }),
    [
      location,
      exceptionalStatuses,
      hasUnsavedDraft,
      mode,
      editingException,
      openCreate,
      openEdit,
      closeDialog,
      draft,
      patchDraft,
      pendingDeleteId,
      requestDelete,
      cancelDelete,
      confirmDelete,
      submit,
      isCreating,
      isUpdating,
      submitError,
    ]
  );

  return (
    <ExceptionDraftContext value={value}>{children}</ExceptionDraftContext>
  );
};
